import { Typography } from '@execonline-inc/execonline-ui';
import { TranslationsContext } from '@execonline-inc/translations';
import { assertNever } from '@kofno/piper';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect } from 'react';
import { T, translation } from '../../../../../Translations';
import AddParticipantIcon from '../../../../Cutesies/AddParticipantIcon';
import Modal from '../../../../Modal';
import { shareStore } from '../Stores/ShareStore';
import { foldInviteTarget, inviteTarget } from '../InviteTarget';
import CollectionHeader from './CollectionHeader';
import { participantSearchStore } from './Stores/ParticipantSearchStore';
import { ready } from './Stores/ParticipantSearchStore/Types';
import { searchOptionsStore } from './Stores/SearchOptionsStore';
import TabNavigation from './TabNavigation';
import Tabs from './Tabs';

const ParticipantSearch: React.FC = () => {
  const ts = React.useContext(TranslationsContext);

  useEffect(() => {
    switch (participantSearchStore.state.kind) {
      case 'waiting':
        participantSearchStore.state = ready();
        searchOptionsStore.load();
        break;
      case 'ready':
      case 'error':
        break;
      default:
        assertNever(participantSearchStore.state);
    }
  }, []);

  const handleAddParticipants = () => {
    switch (participantSearchStore.state.kind) {
      case 'ready':
        const selectedUsers = participantSearchStore.selectedUsers;
        const selectedGroups = participantSearchStore.selectedGroups;

        // Filter out users and groups that are already added
        const newUsers = selectedUsers.filter(
          (user) => !shareStore.existingEmails.has(user.email.toLowerCase()),
        );

        const newGroups = selectedGroups.filter(
          (group) => !shareStore.existingGroupNames.has(group.name.toLowerCase()),
        );

        // Build a combined list of participants to add
        const participantEntries: string[] = [
          ...newUsers.map((user) => user.email),
          ...newGroups.map((group) => group.name),
        ];

        if (participantEntries.length > 0) {
          // Join with newlines and add to invite store
          shareStore.addParticipants(participantEntries.join('\n'));
        }

        // Clear selections and close
        participantSearchStore.clearSelections();
        shareStore.cancelSearch();
        break;
      case 'waiting':
      case 'error':
        break;
      default:
        assertNever(participantSearchStore.state);
    }
  };

  return (
    <Modal
      close={shareStore.cancelSearch}
      bodyPadding="py-6 px-0"
      contentPadding="px-0"
      modalWidth="w-full max-w-[1200px]"
      additionalClasses="h-[calc(100vh-4rem)] overflow-hidden"
      ariaLabel={translation('Select Participants')(ts)}
      headerAction={
        <div className="flex items-center gap-4">
          <button
            type="button"
            className="px-4 py-2 text-primary hover:opacity-80 transition-opacity"
            style={{ fontWeight: 'var(--font-weight-medium)' }}
            onClick={shareStore.cancelSearch}
            data-intercom-target="participant-search-go-back-button"
          >
            <T kind="Go Back" />
          </button>
          <button
            type="button"
            className="px-6 py-2.5 bg-accent text-accent-foreground rounded-md transition-all hover:opacity-90 flex items-center gap-2"
            style={{ fontWeight: 'var(--font-weight-medium)' }}
            onClick={handleAddParticipants}
            data-intercom-target="participant-search-add-selected-button"
          >
            <AddParticipantIcon />
            <T kind="Add selected participants" />
          </button>
        </div>
      }
      title={
        <div className="px-8 mb-8" data-intercom-target="participant-search-header">
          <Typography variant="h5" as="h2" fontWeight="semibold" className="mb-2">
            {inviteTarget(shareStore.state)
              .map((target) =>
                foldInviteTarget(
                  target,
                  () => <T kind="Invite Participants to Collection" />,
                  () => <T kind="Invite Participants to Course" />,
                ),
              )
              .getOrElseValue(<T kind="Invite Participants to Collection" />)}
          </Typography>
        </div>
      }
    >
      <CollectionHeader />
      <TabNavigation />
      <Tabs />
    </Modal>
  );
};

export default observer(ParticipantSearch);
